// ЧТЕНИЕ И ЗАПИСЬ СОХРАНЁННОГО КОНВЕРТА — один ключ, одна форма.
//
// ⚠️ ГРАНИЦА СНЯТА ОСОЗНАННО (cc-126, тикет тихого сохранения `34a19875cbb881c3b015c19af3999a1c`,
// переведён Night → Day решением `s893`). До этой правки здесь стояло «записи нет и быть не
// должно»: оболочке нужно было ровно одно — узнать на монтировании, есть ли под ключом годный
// конверт (приёмка 4 «Каркаса»), а писателя не было вовсе, и граница держалась тем, что писать
// нечем. Теперь писатель пришёл: приёмки 1, 4, 5, 6 тихого сохранения требуют записи, удаления
// ключа на битом значении (`s889.d4` — удаление это тоже запись) и стирания по receipt
// (`s889.d3`). Прежний запрет снят, а не забыт.
//
// ЧЕГО ЗДЕСЬ ПО-ПРЕЖНЕМУ НЕТ — ПОЛИТИКИ. Модуль умеет «прочитать», «записать», «стереть» и
// «проверить, даётся ли запись вообще». КОГДА это делать (уход из поля, смена шага, receipt
// отправки) решает держатель стейта, а не хранилище: иначе правило автосейва размажется по двум
// файлам и разойдётся.
//
// ⚠️ ИМЯ КЛЮЧА — РЕШЕНИЕ CC, И ОНО НЕ ВЕРСИОНИРОВАНО В САМОМ ИМЕНИ. Хром §4 говорит «ключ один,
// версионированный». Версия живёт ВНУТРИ конверта (`schemaVersion`), а не в имени ключа, и иначе
// нельзя: `s889.d4` требует отличать конверт чужой версии от битого, а конверт под именем
// `…wizard.v2` мы бы просто не нашли — ветка `version` стала бы недостижимой, и старый ключ
// остался бы мусором навсегда. Имя стабильное, версия внутри.

import { acceptSession, REJECT } from './session.js';

/** Единственный ключ визарда. Значение под ним — конверт `WizardSession` (`s864.d1`). */
export const WIZARD_STORAGE_KEY = 'lifemap.wizard';

/**
 * Причины, по которым сохранённого конверта нет.
 * К трём причинам самого значения (`absent` / `corrupt` / `version`) добавлена четвёртая:
 * хранилище не далось вовсе (приватный режим, политика). Это не свойство значения, поэтому
 * `acceptSession` о ней знать не обязан.
 */
export const READ_REJECT = { ...REJECT, UNAVAILABLE: 'unavailable' };

/**
 * Строка на случай запрещённого хранилища (`s889.d5`, слово Кирилла — не переписывать).
 * Место и вид — CD по `s885.d2`; здесь живёт только текст, потому что «один раз за сессию»
 * проверяется машинно, а вид — нет.
 */
export const STORAGE_NOTICE = "This browser can't save your progress. Best to finish in one sitting.";

/** Ключ пробной записи. Отдельный от рабочего: проба под рабочим ключом затёрла бы конверт. */
const PROBE_KEY = `${WIZARD_STORAGE_KEY}.probe`;

/**
 * Достать хранилище. Само обращение к `localStorage` бросает в запрещённом — до всякой операции,
 * поэтому доступ обёрнут, а не проверен условием.
 */
function resolveStorage(storage) {
  if (storage) return storage;
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

/**
 * Прочитать конверт из хранилища.
 *
 * @param {Storage} [storage] по умолчанию `window.localStorage`
 * @returns {{session: object}|{reason: string}} причина НАЗВАНА — вызывающий не выводит её сам
 */
export function readSession(storage) {
  const store = resolveStorage(storage);
  if (!store) return { reason: READ_REJECT.UNAVAILABLE };

  let raw;
  try {
    raw = store.getItem(WIZARD_STORAGE_KEY);
  } catch {
    return { reason: READ_REJECT.UNAVAILABLE };
  }

  if (raw === null || raw === undefined) return { reason: READ_REJECT.ABSENT };

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // Разбор строки — дело владельца ключа, поэтому `SyntaxError` не уходит наружу: у оболочки
    // одна ветка «годного конверта нет», с названной причиной.
    return { reason: READ_REJECT.CORRUPT };
  }

  return acceptSession(parsed);
}

/**
 * Записать конверт под единственный ключ. Сериализация целиком, без слияния с тем, что лежало:
 * конверт — один объект (`s864.d1`), частичная запись развела бы две его половины.
 *
 * Отказ НЕ бросается наружу: в запрещённом хранилище визард обязан идти дальше без сохранения
 * (`s889.d5`), а решать, показывать ли строку, — дело держателя стейта.
 *
 * @returns {{ok: true}|{ok: false, reason: string}}
 */
export function writeSession(session, storage) {
  const store = resolveStorage(storage);
  if (!store) return { ok: false, reason: READ_REJECT.UNAVAILABLE };
  try {
    store.setItem(WIZARD_STORAGE_KEY, JSON.stringify(session));
    return { ok: true };
  } catch {
    // Квота и запрет политики отличаются классом исключения, но не следствием: записи нет.
    // Различать их здесь значило бы обещать поведение, которого у визарда нет.
    return { ok: false, reason: READ_REJECT.UNAVAILABLE };
  }
}

/**
 * Стереть ключ. Две причины стирания: отвергнутое значение (`s889.d4` — битое или чужой версии)
 * и подтверждённая отправка (`s889.d3`). Обе — политика вызывающего, здесь только действие.
 *
 * ⚠️ Стирание ключа ≠ `start over`. Сброс пишет в ключ сброшенный конверт с тем же `sessionId`
 * (`s866.d1`): стереть его значит осиротить будущий resume-токен Track D.
 *
 * @returns {{ok: true}|{ok: false, reason: string}}
 */
export function clearSession(storage) {
  const store = resolveStorage(storage);
  if (!store) return { ok: false, reason: READ_REJECT.UNAVAILABLE };
  try {
    store.removeItem(WIZARD_STORAGE_KEY);
    return { ok: true };
  } catch {
    return { ok: false, reason: READ_REJECT.UNAVAILABLE };
  }
}

/**
 * Проба записи на старте (`s889.d5`): даётся ли хранилище вообще.
 *
 * Пробуем ОТДЕЛЬНЫМ ключом и за собой убираем. Под рабочим ключом проба затёрла бы сохранённый
 * конверт человека — то есть проверка «смогу ли я сохранить» уничтожила бы то, что уже сохранено.
 *
 * @returns {{ok: true}|{ok: false, reason: string}}
 */
export function probeStorage(storage) {
  const store = resolveStorage(storage);
  if (!store) return { ok: false, reason: READ_REJECT.UNAVAILABLE };
  try {
    store.setItem(PROBE_KEY, '1');
    store.removeItem(PROBE_KEY);
    return { ok: true };
  } catch {
    return { ok: false, reason: READ_REJECT.UNAVAILABLE };
  }
}
